import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit'
import { LeadService } from 'services/crm'

const leadsAdapter = createEntityAdapter({
  sortComparer: (a, b) => (a.updatedAt < b.updatedAt ? 1 : -1),
})

export const fetchLeads = createAsyncThunk(
  'crm/leads/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await LeadService.getAll(params)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching leads')
    }
  },
)

export const fetchLead = createAsyncThunk(
  'crm/leads/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const res = await LeadService.get(id)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching lead')
    }
  },
)

export const createLead = createAsyncThunk(
  'crm/leads/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await LeadService.create(data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error creating lead')
    }
  },
)

export const updateLead = createAsyncThunk(
  'crm/leads/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await LeadService.update(id, data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error updating lead')
    }
  },
)

export const removeLead = createAsyncThunk(
  'crm/leads/remove',
  async (id, { rejectWithValue }) => {
    try {
      await LeadService.remove(id)
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error deleting lead')
    }
  },
)

export const moveLeadStage = createAsyncThunk(
  'crm/leads/move',
  async ({ leadId, stageId }, { rejectWithValue }) => {
    try {
      const res = await LeadService.move(leadId, { stageId })
      return res.data
    } catch (e) {
      return rejectWithValue({
        leadId,
        message: e.response?.data?.message || 'Error moving lead',
      })
    }
  },
)

// Convert returns { lead, deal } — we upsert the lead and surface the deal
// to the caller so it can navigate.
export const convertLead = createAsyncThunk(
  'crm/leads/convert',
  async ({ leadId, payload }, { rejectWithValue }) => {
    try {
      const res = await LeadService.convert(leadId, payload || {})
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error converting lead')
    }
  },
)

const initialState = leadsAdapter.getInitialState({
  loading: 'idle',
  error: null,
})

const leadsSlice = createSlice({
  name: 'crmLeads',
  initialState,
  reducers: {
    optimisticMove: (state, action) => {
      const { leadId, stageId } = action.payload
      const lead = state.entities[leadId]
      if (lead) lead.stageId = stageId
    },
    rollbackMove: (state, action) => {
      const { leadId, previousStageId } = action.payload
      const lead = state.entities[leadId]
      if (lead) lead.stageId = previousStageId
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeads.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = 'idle'
        leadsAdapter.setAll(state, action.payload)
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = 'idle'
        state.error = action.payload
      })
      .addCase(fetchLead.fulfilled, (state, action) => {
        leadsAdapter.upsertOne(state, action.payload)
      })
      .addCase(createLead.fulfilled, (state, action) => {
        leadsAdapter.upsertOne(state, action.payload)
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        leadsAdapter.upsertOne(state, action.payload)
      })
      .addCase(removeLead.fulfilled, (state, action) => {
        leadsAdapter.removeOne(state, action.payload)
      })
      .addCase(moveLeadStage.fulfilled, (state, action) => {
        leadsAdapter.upsertOne(state, action.payload)
      })
      .addCase(convertLead.fulfilled, (state, action) => {
        if (action.payload?.lead) {
          leadsAdapter.upsertOne(state, action.payload.lead)
        }
      })
  },
})

export const { optimisticMove, rollbackMove } = leadsSlice.actions

const selectors = leadsAdapter.getSelectors((state) => state.crmLeads)
export const selectAllLeads = selectors.selectAll
export const selectLeadById = selectors.selectById

export const selectLeadsByPipeline = (pipelineId) =>
  createSelector([selectAllLeads], (leads) =>
    leads.filter((l) => l.pipelineId === pipelineId),
  )

export const selectLeadsByStage = (stageId) =>
  createSelector([selectAllLeads], (leads) =>
    leads
      .filter((l) => l.stageId === stageId)
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
  )

export default leadsSlice.reducer
